import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import css from "./Hero.module.css";

interface BannerProps {
  alt: string;
  url: string;
  urlMetaTitle?: string; // Add urlMetaTitle property for SEO
  display: string;
  image: string;
  title: string;
  subtitle: string;
  buttonText: string;
}

// Accept isMd prop to determine the screen size
interface HeroProps {
  banners: BannerProps[]; // Accept banners as a prop
}

const Hero: React.FC<HeroProps> = ({ banners }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [autoplay, setAutoplay] = React.useState(false); // Add useState for autoplay
  const sliderRef = useRef<Slider>(null); // Add useRef for slider

  // Utility to set tabindex="-1" on all focusable elements inside hidden slides
  const setTabIndexOnHiddenSlides = () => {
    // Select all focusable elements inside hidden slides
    const focusableSelectors = [
      "a",
      "button",
      "input",
      "select",
      "textarea",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");
    const hiddenSlides = document.querySelectorAll(
      '.slick-slide[aria-hidden="true"]'
    );
    hiddenSlides.forEach((slide) => {
      const focusables = slide.querySelectorAll(focusableSelectors);
      focusables.forEach((el) => {
        el.setAttribute("tabindex", "-1");
      });
    });
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplaySpeed: 5000,
    arrows: false,
    appendDots: (dots: React.ReactNode) => (
      <div style={{ position: "relative" }}>
        <ul>{dots}</ul>
      </div>
    ),
    dotsClass: "slick-dots",
    afterChange: (index: number) => {
      console.log("Hero.tsx afterChange triggered"); // Add console.log
      // Explicitly define the type of index
      setTabIndexOnHiddenSlides();
    },
  };

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Find the first desktop banner for preloading
  const firstDesktopBanner = banners.find(
    (banner) => banner.display === "desktop" && banner.image
  );

  // Filter images based on display (mobile or desktop) for the slider
  const filteredBanners = banners.filter(
    (banner) =>
      (isMobile ? banner.display === "mobile" : banner.display === "desktop") &&
      banner.image
  );

  React.useEffect(() => {
    // Add useEffect for autoplay delay
    setTimeout(() => {
      setAutoplay(true); // Enable autoplay after delay
      if (sliderRef.current) {
        sliderRef.current.slickPlay(); // Re-initialize slider
      }
    }, 3000); // Delay autoplay by 3 seconds
  }, []);

  // Ensure tabindex is set on initial mount and whenever banners/isMobile changes
  React.useEffect(() => {
    setTimeout(() => {
      setTabIndexOnHiddenSlides();
    }, 0);
  }, [banners, isMobile]);

  return (
    <div className={css.hero}>
      <Helmet>
        {/* Preload the first desktop banner image */}
        {firstDesktopBanner && (
          <link
            rel="preload"
            as="image"
            href={`${firstDesktopBanner.image}`}
            fetchPriority="high" // Corrected: camelCase
          />
        )}
      </Helmet>
      <Slider
        {...settings}
        autoplay={autoplay}
        autoplaySpeed={settings.autoplaySpeed}
        ref={sliderRef}
      >
        {filteredBanners.map((banner, index) => (
          <div key={`banner-${index}`}>
            <a
              href={banner.url}
              title={
                banner.urlMetaTitle ||
                banner.title ||
                banner.alt ||
                `Banner ${index + 1}`
              }
            >
              <img
                src={`${banner.image}`}
                alt={banner.alt}
                title={
                  banner.title ||
                  banner.alt ||
                  `Banner promocional ${index + 1}`
                } // Add title attribute
                className={css.sliderImage}
                aria-label={
                  banner.title || banner.alt || `Hero banner ${index + 1}`
                }
                {...(index === 0 && !isMobile
                  ? { fetchpriority: "high" as const }
                  : { fetchpriority: "auto" as const })}
                loading={index === 0 && !isMobile ? "eager" : "lazy"} // Eager load the first visible banner
              />
            </a>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default Hero;
